import { Localization } from "@viamap/viamap2-common";
import { PLAProject } from "./PLAState";
import { validatePLAExpression } from "./PLAExpression";

export type PLAProjectExpressionIssueKind = "elementType" | "attribute" | "alert";

export type PLAProjectExpressionIssue = {
   kind: PLAProjectExpressionIssueKind;
   elementTypeId: string;
   attributeId?: string;
   alertId?: string;
   message: string;
};

function elementTypeLabel(project: PLAProject, elementTypeId: string) {
   const elementType = project.elementTypes[elementTypeId];
   return elementType?.name ? `${elementType.name} (${elementTypeId})` : elementTypeId;
}

function attributeLabel(project: PLAProject, elementTypeId: string, attributeId: string) {
   const attribute = project.elementTypes[elementTypeId]?.attributes?.[attributeId];
   return attribute?.name ? `${attribute.name} (${attributeId})` : attributeId;
}

function alertLabel(project: PLAProject, elementTypeId: string, attributeId: string, alertId: string) {
   const alert = project.elementTypes[elementTypeId]?.attributes?.[attributeId]?.alerts?.[alertId];
   return alert?.alertMessage ? `${alert.alertMessage} (${alertId})` : alertId;
}

export function formatPLAProjectExpressionIssue(project: PLAProject, issue: PLAProjectExpressionIssue) {
   if (issue.kind === "alert" && issue.attributeId && issue.alertId) {
      return `${Localization.getText("Invalid alert expression")}: ${elementTypeLabel(project, issue.elementTypeId)} > ${attributeLabel(project, issue.elementTypeId, issue.attributeId)} > ${alertLabel(project, issue.elementTypeId, issue.attributeId, issue.alertId)}: ${issue.message}`;
   }
   if (issue.kind === "attribute" && issue.attributeId) {
      return `${Localization.getText("Invalid expression")}: ${elementTypeLabel(project, issue.elementTypeId)} > ${attributeLabel(project, issue.elementTypeId, issue.attributeId)}: ${issue.message}`;
   }
   return `${Localization.getText("Invalid expression")}: ${elementTypeLabel(project, issue.elementTypeId)}: ${issue.message}`;
}

export function collectPLAProjectExpressionIssues(project: PLAProject): PLAProjectExpressionIssue[] {
   const issues: PLAProjectExpressionIssue[] = [];
   Object.entries(project.elementTypes || {}).forEach(([elementTypeId, elementType]) => {
      Object.entries(elementType.attributes || {}).forEach(([attributeId, attribute]) => {
         if (attribute.type === "expression") {
            const validation = typeof attribute.expression === "string"
               ? validatePLAExpression(attribute.expression, { project, elementTypeId })
               : { valid: false, error: Localization.getText("Expression is required") };
            if (!validation.valid) issues.push({ kind: "attribute", elementTypeId, attributeId, message: validation.error || Localization.getText("Invalid expression") });
         }
         Object.entries(attribute.alerts || {}).forEach(([alertId, alert]) => {
            const validation = validatePLAExpression(alert.booleanExpression || "", { project, elementTypeId });
            if (!validation.valid) issues.push({ kind: "alert", elementTypeId, attributeId, alertId, message: validation.error || Localization.getText("Invalid alert expression") });
         });
      });
   });
   return issues;
}
